// PRO Deployment cross-page dirty-list tracker.
//
// Canonical: PRD 21 §10 (PRO 배포 정책). This store is the FE-side single
// source of truth for the dirty state shown by the red "수정됨" header badge
// on PRO Deployment Management (PRD 06), the yellow "수정" badges next to
// items on PRD 04 (PRO 항목 설계), PRD 05 (문항 설계), and PRD 07 (관리지침),
// and the enable-state of the [PRO 배포] button.
//
// Object shape (PRD 21 §10.1):
//   { items:        string[]      // PID list — set by PRD 04 PUBLISHED save/delete.
//     surveys:      string[]      // QID list — set by PRD 05 add/update/delete.
//     guidelines:   string[]      // GID list — set by PRD 07 PUBLISHED save.
//     moduleConfig: boolean       // Set by PRD 06 page-self edits. }
//
// Persisted to sessionStorage under `mcDeployDirtyItems` to mirror the
// prototype: a tab refresh keeps the badges visible until PRO 배포 관리's
// deploy step clears the list via `clearAll()`.

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Stored as string[] (not Set) so JSON serialization is trivial; the adders
// deduplicate and reads treat membership as set-like.
interface DeployDirtyState {
  items: string[];
  surveys: string[];
  guidelines: string[];
  moduleConfig: boolean;

  // Per-category dirty adders (idempotent). PRD 04 / 05 / 07.
  addItem: (pid: string) => void;
  addSurvey: (qid: string) => void;
  addGuideline: (gid: string) => void;
  // PRD 06 — page-self edits (module / scope config).
  setModuleConfig: (value: boolean) => void;

  // Reads.
  isItemDirty: (pid: string) => boolean;
  isSurveyDirty: (qid: string) => boolean;
  isGuidelineDirty: (gid: string) => boolean;
  // True when anything is dirty — drives the 수정됨 badge + [PRO 배포] button.
  isDirty: () => boolean;

  // Cleared by PRO Deployment Management after a successful [PRO 배포].
  clearAll: () => void;
}

const EMPTY: Pick<DeployDirtyState, 'items' | 'surveys' | 'guidelines' | 'moduleConfig'> = {
  items: [],
  surveys: [],
  guidelines: [],
  moduleConfig: false,
};

const addUnique = (list: string[], id: string): string[] =>
  list.includes(id) ? list : [...list, id];

export const useDeployDirtyStore = create<DeployDirtyState>()(
  persist(
    (set, get) => ({
      ...EMPTY,

      addItem: (pid) => set((s) => ({ items: addUnique(s.items, pid) })),
      addSurvey: (qid) => set((s) => ({ surveys: addUnique(s.surveys, qid) })),
      addGuideline: (gid) => set((s) => ({ guidelines: addUnique(s.guidelines, gid) })),
      setModuleConfig: (value) => set({ moduleConfig: value }),

      isItemDirty: (pid) => get().items.includes(pid),
      isSurveyDirty: (qid) => get().surveys.includes(qid),
      isGuidelineDirty: (gid) => get().guidelines.includes(gid),
      isDirty: () => {
        const s = get();
        return (
          s.items.length > 0 ||
          s.surveys.length > 0 ||
          s.guidelines.length > 0 ||
          s.moduleConfig
        );
      },

      clearAll: () => set({ ...EMPTY }),
    }),
    {
      name: 'mcDeployDirtyItems',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist the data — methods are recreated on rehydrate. New fields
      // default via `EMPTY` if an older shape is rehydrated from sessionStorage.
      partialize: (s) => ({
        items: s.items,
        surveys: s.surveys,
        guidelines: s.guidelines,
        moduleConfig: s.moduleConfig,
      }),
      merge: (persisted, current) => ({
        ...current,
        ...EMPTY,
        ...(persisted as Partial<DeployDirtyState>),
      }),
    },
  ),
);
